import axios, { AxiosInstance } from 'axios'

export interface FraudReason {
  type: string
  description: string
  confidence: number
}

export interface FraudDetectionResult {
  candidate_id: string
  candidate_name: string
  candidate_email: string | null
  is_suspicious: boolean
  confidence_score: number
  reasons: FraudReason[]
  detected_at: string
}

export interface ScanSummary {
  total_candidates: number
  suspicious_candidates: number
  flagged_rate: number
}

export interface Scan {
  scan_id: string
  status: 'in_progress' | 'completed' | 'failed'
  trigger: 'scheduled' | 'manual'
  started_at: string
  completed_at: string | null
  summary: ScanSummary | null
  results: FraudDetectionResult[]
  error_message: string | null
}

class ApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('okta-token-storage')
      if (token) {
        try {
          const parsed = JSON.parse(token)
          const accessToken = parsed?.accessToken?.accessToken
          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`
          }
        } catch (e) {
          console.error('Failed to parse token:', e)
        }
      }
      return config
    })
  }

  async triggerScan(daysFilter?: number): Promise<{ scan_id: string; message: string }> {
    const response = await this.client.post('/scans', {
      days_filter: daysFilter,
    })
    return response.data
  }

  async listScans(): Promise<Scan[]> {
    const response = await this.client.get('/scans')
    return response.data
  }

  async getScan(scanId: string): Promise<Scan> {
    const response = await this.client.get(`/scans/${scanId}`)
    return response.data
  }

  async searchCandidates(query: string): Promise<any[]> {
    const response = await this.client.get('/candidates/search', {
      params: { q: query }
    })
    return response.data
  }

  async scanIndividualCandidate(candidateId: string): Promise<FraudDetectionResult> {
    const response = await this.client.post('/candidates/scan', {
      candidate_id: candidateId
    })
    return response.data
  }

  async clearAllScans(): Promise<{ message: string; deleted_count: number }> {
    const response = await this.client.delete('/scans')
    return response.data
  }

  async chat(
    message: string,
    conversationHistory: Array<{ role: string; content: string }>
  ): Promise<{ message: string; model: string; usage: { input_tokens: number; output_tokens: number } }> {
    const response = await this.client.post('/chat', {
      message,
      conversation_history: conversationHistory,
    })
    return response.data
  }
}

export const apiService = new ApiService()
